import React, { useCallback, useEffect, useState } from "react";
import { Grid, Container, Box, Drawer, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Categories from "../Layout/Categories/Categories";
import Teams from "../Layout/Teams/Teams";
import Timeline from "../Layout/Timeline/Timeline";
import PostCards from "../Layout/Postcards/Postcards";
import ComposePage from "../../pages/compose/ComposePage";
import Settings from "../Layout/Settings/Setting";
import TeamDetails from "../Layout/Teams/TeamDetails/Teamsdetails";
import UserDetails from "../Layout/Userdetails/Userdetails";
import ActivitiesAndTopPosts from "../Layout/ActivitiesAndTopPosts/Activitiesandtopposts";
import ProfileDetails from "../../pages/profile";
import useScreenSize from "../../shared/useScreensize";
import { useLocation } from "react-router-dom";
import UserService from "../../services/categoryService";
import { Auth } from "../../contexts/AuthContext";
import Notifications from "../Layout/notifications";
import "./Dashboard.css";

const Dashboard = () => {
  const { isDesktopScreen } = useScreenSize();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const path = useLocation();
  const { userProfile, setUserProfile, setLoyaltyPoints } = Auth();
  const isAdmin = JSON.parse(sessionStorage.getItem("role") || "[]").flat().includes("ROLE_ADMIN");

  const getPageTitle = () => {
    if (path.pathname === "/employees") {
      return "DashBoard";
    }
    if (path.pathname === "/employees/timeline") {
      return "Timeline";
    }
    if (path.pathname === "/employees/settings") {
      return "Settings";
    }
    if (path.pathname === "/employees/notifications") {
      return "Notifications";
    }
    if (path.pathname === "/employees/profile") {
      return "Profile";
    }
    if (path.pathname === "/employees/compose") {
      return "Compose";
    }
    return path.pathname.split("/").filter(Boolean).pop();
  };

  const fetchProfile = useCallback(async () => {
    try {
      const response = await UserService?.getProfile();
      if (response?.status === true) {
        setUserProfile(response?.data);
      }
    } catch (error) {
      console.log(error);
    }
  }, [setUserProfile]);

  const fetchLoyaltyPoints = useCallback(async () => {
    try {
      const response = await UserService?.getLoyaltyPoints();
      if (response?.status === true) {
        setLoyaltyPoints(response?.loyalty);
      }
    } catch (error) {
      console.log(error);
    }
  }, [setLoyaltyPoints]);

  useEffect(() => {
    fetchLoyaltyPoints();
    fetchProfile();
  }, [fetchLoyaltyPoints, fetchProfile]);

  const renderEmployeeContent = () => {
    if (path.pathname === "/employees/settings") {
      return <Settings />;
    }

    if (path.pathname === "/employees/profile") {
      return <ProfileDetails />;
    }

    if (path.pathname === "/employees/notifications") {
      return <Notifications />;
    }

    if (path.pathname.startsWith("/employees/team-member/")) {
      const memberId = path.pathname.split("/").pop();
      return (
        <Box>
          <TeamDetails memberId={memberId} />
        </Box>
      );
    }

    if (path.pathname === "/employees/compose") {
      return <ComposePage userProfile={userProfile} />;
    }

    // Timeline — feed only, no compose block
    return (
      <Box>
        <PostCards />
      </Box>
    );
  };

  return (
    <Container
      disableGutters
      maxWidth={false}
      sx={{
        width: "100vw",
        overflowX: "hidden",
        backgroundColor: "#F9F5FF",
        minHeight: "100vh",
        padding: { xs: "72px 10px 15px 10px", sm: "72px 15px 15px 15px", md: "0px 20px 15px 15px" },
        boxSizing: "border-box",
      }}
    >
      {!isDesktopScreen && (
        <>
          <Box sx={{ width: "100%", position: "fixed", top: 0, left: 0, zIndex: 999 }}>
            <Timeline
              pageTitle={getPageTitle()}
              onClickChangeView={() => setMobileSidebarOpen(true)}
              userProfile={userProfile}
            />
          </Box>
          <Drawer
            anchor="left"
            open={mobileSidebarOpen}
            onClose={() => setMobileSidebarOpen(false)}
            PaperProps={{
              sx: {
                width: "80%",
                maxWidth: 300,
                backgroundColor: "#F9F5FF",
                display: "flex",
                flexDirection: "column",
                overflowY: "auto",
                position: "relative",
              },
            }}
          >
            <IconButton
              onClick={() => setMobileSidebarOpen(false)}
              sx={{ position: "absolute", top: 8, right: 8, zIndex: 10 }}
            >
              <CloseIcon />
            </IconButton>
            <Box sx={{ flex: 1, overflowY: "auto", px: 1, pb: 2 }}>
              <Categories />
              <Teams />
            </Box>
          </Drawer>
          <Grid container spacing={3} sx={{ height: "100%" }}>
            <Grid item xs={12}>
              {renderEmployeeContent()}
            </Grid>
          </Grid>
        </>
      )}

      {isDesktopScreen && (
        <Grid container spacing={2} sx={{ height: "100%" }}>
          <Grid item xs={12} md={3} sx={{ height: "100%" }}>
            <Box className="leftSideBar">
              <Categories />
              <Teams />
            </Box>
          </Grid>

          <Grid
            item
            xs={12}
            md={9}
            sx={{
              boxSizing: "border-box",
              backgroundColor: "#faf8ff",
              overflowX: "hidden",
            }}
          >
            <Box
              sx={{
                mb: 2,
                width: "100%",
                position: "sticky",
                top: 0,
                zIndex: 999,
                backgroundColor: "#fff",
                borderRadius: "16px",
                boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.05)",
              }}
            >
              <Timeline pageTitle={getPageTitle()} userProfile={userProfile} />
            </Box>
            <Grid container spacing={path.pathname === "/employees/compose" ? 0 : 3}>
              <Grid
                item
                xs={12}
                md={12}
                sx={{
                  height: {
                    xs: "auto",
                    sm: "auto",
                    md: path.pathname === "/employees/compose" ? "calc(100vh - 84px)" : "calc(130vh - 72px)",
                  },
                  overflowY: {
                    xs: "visible",
                    sm: "visible",
                    md: "auto",
                  },
                  overflowX: "hidden",
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                  "&::-webkit-scrollbar": {
                    display: "none",
                  },
                  ...(path.pathname === "/employees/compose" && { p: "0 !important", display: "flex", flexDirection: "column" }),
                }}
                className="postContentContainer"
              >
                {renderEmployeeContent()}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

export default Dashboard;

